import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { featureFlagsApi } from '../services/api';
import { 
  PageHeader, 
  SearchBar, 
  LoadingSpinner, 
  EmptyState, 
  ListContainer, 
  Pagination,
  Badge,
  ErrorMessage,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Button,
  FormSelect,
  ConfirmationModal
} from '../components/common';
import { Flag, Plus, Eye, Trash2, ToggleLeft, ToggleRight, Building, User, List } from 'lucide-react';

const FeatureFlags = () => {
  const navigate = useNavigate();
  const [featureFlags, setFeatureFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [featureTypes, setFeatureTypes] = useState([]);
  const [selectedFeatureType, setSelectedFeatureType] = useState('');
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [isEnabledFilter, setIsEnabledFilter] = useState('');
  const [deleteModal, setDeleteModal] = useState({ show: false, flagId: null });
  const [toggleLoading, setToggleLoading] = useState({});

  const fetchFeatureFlags = async () => {
    try {
      setLoading(true);
      const params = {
        pageNumber: currentPage,
        pageSize: pageSize,
        searchString: searchString || undefined,
        featureType: selectedFeatureType || undefined,
        buildingId: selectedBuildingId || undefined,
        isEnabled: isEnabledFilter !== '' ? isEnabledFilter === 'true' : undefined
      };

      const response = await featureFlagsApi.list(params);
      setFeatureFlags(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch feature flags');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatureTypes = async () => {
    try {
      // Get all available feature types from the dedicated endpoint
      const typesResponse = await featureFlagsApi.getTypes();
      const typesData = typesResponse.data || [];
      
      let formattedTypes = [];
      
      if (typesData.length > 0 && typeof typesData[0] === 'object') {
        // If the API returns objects with properties
        formattedTypes = typesData.map(typeObj => ({
          value: typeObj.value || typeObj.id || typeObj,
          name: typeObj.name || typeObj.displayName || `Feature Type ${typeObj.value || typeObj.id || typeObj}`
        }));
      } else {
        // If the API returns simple values
        const uniqueTypes = [...new Set(typesData)];
        formattedTypes = uniqueTypes.map(typeValue => ({
          value: typeValue,
          name: `Feature Type ${typeValue}`
        }));
      }
      
      // Sort by value
      formattedTypes.sort((a, b) => a.value - b.value);
      setFeatureTypes(formattedTypes);
    } catch (err) {
      console.error('Failed to fetch feature types:', err);
    }
  };

  useEffect(() => {
    fetchFeatureFlags();
  }, [currentPage, searchString, selectedFeatureType, selectedBuildingId, isEnabledFilter]);

  useEffect(() => {
    fetchFeatureTypes();
  }, []);

  const handleSearch = (value) => {
    setSearchString(value);
    setCurrentPage(1);
  };

  const handleToggleFeature = async (flagId, currentStatus) => {
    try {
      setToggleLoading(prev => ({ ...prev, [flagId]: true }));
      
      const flag = featureFlags.find(f => f.userFeatureFlagId === flagId);
      if (!flag) return;

      const toggleData = {
        userId: flag.userId,
        featureType: flag.featureType,
        isEnabled: !currentStatus
      };
      
      // Only include buildingId if it exists
      if (flag.buildingId) {
        toggleData.buildingId = flag.buildingId;
      }

      await featureFlagsApi.toggle(toggleData);
      await fetchFeatureFlags();
    } catch (err) {
      setError(err.message || 'Failed to toggle feature flag');
    } finally {
      setToggleLoading(prev => ({ ...prev, [flagId]: false }));
    }
  };

  const handleDeleteFlag = async () => {
    try {
      await featureFlagsApi.delete(deleteModal.flagId);
      setDeleteModal({ show: false, flagId: null });
      await fetchFeatureFlags();
    } catch (err) {
      setError(err.message || 'Failed to delete feature flag');
    }
  };

  const getFeatureTypeName = (flag) => {
    // Use the featureTypeName from the API response if available
    return flag.featureTypeName || `Feature Type ${flag.featureType}`;
  };

  const getBadgeVariant = (isEnabled) => {
    return isEnabled ? 'success' : 'secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && featureFlags.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Feature Flags"
        subtitle={`Manage feature flags for users and buildings (${totalCount} total)`}
        icon={Flag}
        action={
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/feature-flags/types')}
            >
              <List size={16} className="mr-2" />
              Feature Types
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate('/feature-flags/create')}
            >
              <Plus size={16} className="mr-2" />
              Create Feature Flag
            </Button>
          </div>
        }
      />

      {error && <ErrorMessage message={error} />}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div>
          <SearchBar
            value={searchString}
            onSearch={handleSearch}
            placeholder="Search users, buildings..."
          />
        </div>
        
        <div>
          <FormSelect
            value={selectedFeatureType}
            onChange={(e) => {
              setSelectedFeatureType(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: '', label: 'All Feature Types' },
              ...featureTypes.map(type => ({
                value: type.value,
                label: type.name
              }))
            ]}
          />
        </div>

        <div>
          <FormSelect
            value={isEnabledFilter}
            onChange={(e) => {
              setIsEnabledFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'true', label: 'Enabled' },
              { value: 'false', label: 'Disabled' }
            ]}
          />
        </div>

        <div>
          <input
            type="text"
            placeholder="Building ID..."
            value={selectedBuildingId}
            onChange={(e) => {
              setSelectedBuildingId(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      <ListContainer>
        {featureFlags.length === 0 ? (
          <EmptyState
            title="No feature flags found"
            description="No feature flags match your current filters."
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <tr>
                  <TableCell header>User</TableCell>
                  <TableCell header>Building</TableCell>
                  <TableCell header>Feature Type</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Created</TableCell>
                  <TableCell header>Actions</TableCell>
                </tr>
              </TableHeader>
              <TableBody>
                {featureFlags.map((flag) => (
                  <TableRow key={flag.userFeatureFlagId}>
                    <TableCell>
                      <div className="flex items-center">
                        <User size={16} className="mr-2 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">
                            {flag.fullName || flag.userName || 'Unknown User'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {flag.email || flag.userId}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {flag.buildingId ? (
                        <div className="flex items-center">
                          <Building size={16} className="mr-2 text-gray-400" />
                          <div>
                            <div className="font-medium text-gray-900">
                              {flag.buildingName || 'Unknown Building'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {flag.buildingId}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-500">Global</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="info" 
                        className="cursor-pointer hover:bg-blue-100 transition-colors"
                        onClick={() => navigate(`/feature-flags/types/${flag.featureType}`)}
                      >
                        {getFeatureTypeName(flag)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getBadgeVariant(flag.isEnabled)}>
                        {flag.isEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {formatDate(flag.createdDate)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleToggleFeature(flag.userFeatureFlagId, flag.isEnabled)}
                          disabled={toggleLoading[flag.userFeatureFlagId]}
                          className="p-1 text-primary-600 hover:text-primary-800 disabled:opacity-50"
                          title={flag.isEnabled ? 'Disable' : 'Enable'}
                        >
                          {toggleLoading[flag.userFeatureFlagId] ? (
                            <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : flag.isEnabled ? (
                            <ToggleRight size={16} />
                          ) : (
                            <ToggleLeft size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => setDeleteModal({ show: true, flagId: flag.userFeatureFlagId })}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalCount={totalCount}
              pageSize={pageSize}
            />
          </>
        )}
      </ListContainer>

      <ConfirmationModal
        isOpen={deleteModal.show}
        onClose={() => setDeleteModal({ show: false, flagId: null })}
        onConfirm={handleDeleteFlag}
        title="Delete Feature Flag"
        message="Are you sure you want to delete this feature flag? This action cannot be undone."
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default FeatureFlags;